/**
 * lib/studentPhoto.ts
 * ------------------------------------------------------------------
 * Putting a photo on a pupil's record, showing it, and taking it down —
 * from the browser's side.
 *
 * WHAT CHANGED, AND WHY. This used to upload straight to the live photo
 * and save a Firebase "download URL" on the pupil. A download URL opens
 * the image for anybody who holds it, signed in or not, for as long as it
 * exists: forwarded once, a child's photograph is public. Parents can now
 * upload too, which made that unacceptable. No photo had ever been stored,
 * so nothing was exposed; the change was made before anything could be.
 *
 * NOW:
 *   UPLOAD goes to a holding area the browser can write to and never read.
 *   A server function re-draws it — dropping the GPS coordinates a phone
 *   embeds — writes the live photo with no download token, records it on
 *   the pupil, and deletes what was sent. See functions/studentPhotos.js.
 *
 *   SHOWING a photo fetches the bytes through the storage rules every
 *   time (getBlob), so only staff and that child's own parents ever see
 *   it. The object URL made from those bytes lives only in this tab.
 *
 *   TAKING ONE DOWN is a server call, checked against who is asking.
 *
 * The browser shrinks and re-encodes the photo before sending it. That is
 * to spare a parent's mobile data, NOT a safety measure: a tampered browser
 * could skip it, which is exactly why the server re-draws everything.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { ref, uploadBytes, getBlob } from 'firebase/storage';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { storage, db, functions } from '@/lib/firebase';
import {
  uploadPath, uploadFileProblem, CLIENT_MAX_EDGE, PHOTO_CONSENT_VERSION,
  PHOTO_UPLOAD_STATUS_COLLECTION, type StudentPhoto,
} from '@/lib/studentPhotoPolicy';

/* ===================== preparing the file ========================== */

/**
 * Re-encode to JPEG with the longest edge capped. Returns null when the
 * browser cannot read the file at all — typically an iPhone HEIC photo in
 * a browser that cannot decode HEIC — so the caller can say so plainly.
 */
export async function prepareForUpload(file: File, maxPx = CLIENT_MAX_EDGE): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) { resolve(null); return; }
      if (w > maxPx || h > maxPx) {
        const r = Math.min(maxPx / w, maxPx / h);
        w = Math.round(w * r);
        h = Math.round(h * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

const newUploadId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/* ===================== uploading =================================== */

export type UploadOutcome =
  | { status: 'done' }
  | { status: 'rejected'; reason: string }
  | { status: 'timeout' };

/**
 * Send a photo and wait for the server's verdict.
 *
 * `consent` is required for a parent — the rules refuse a parent's upload
 * without it — and ignored for staff.
 */
export async function uploadStudentPhoto(args: {
  studentId: string;
  file: File;
  uploadedBy: string;
  consent?: boolean;
  timeoutMs?: number;
}): Promise<UploadOutcome> {
  const bad = uploadFileProblem(args.file);
  if (bad) throw new Error(bad);

  const blob = await prepareForUpload(args.file);
  if (!blob) {
    throw new Error(
      'This photo could not be opened here. Try a JPG — on an iPhone, choose the photo again '
      + 'and pick "Most Compatible", or take a screenshot of it.',
    );
  }

  const uploadId = newUploadId();
  const outcome = waitForOutcome(uploadId, args.timeoutMs ?? 60_000);

  await uploadBytes(ref(storage, uploadPath(args.studentId, uploadId)), blob, {
    contentType: 'image/jpeg',
    customMetadata: {
      uploadedBy: args.uploadedBy,
      ...(args.consent ? { consent: PHOTO_CONSENT_VERSION } : {}),
    },
  });

  return outcome;
}

/** Resolves when the server has finished with an upload, either way. */
function waitForOutcome(uploadId: string, timeoutMs: number): Promise<UploadOutcome> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (o: UploadOutcome) => {
      if (settled) return;
      settled = true;
      unsub();
      clearTimeout(timer);
      resolve(o);
    };
    const unsub = onSnapshot(
      doc(db, PHOTO_UPLOAD_STATUS_COLLECTION, uploadId),
      (snap) => {
        const d = snap.data() as { status?: string; reason?: string } | undefined;
        if (d?.status === 'done') finish({ status: 'done' });
        else if (d?.status === 'rejected') finish({ status: 'rejected', reason: d.reason || 'The photo was not accepted.' });
      },
      // Not readable until the server writes it with our uid — that first
      // refusal is expected. Keep waiting; the timeout is the backstop.
      () => {},
    );
    const timer = setTimeout(() => finish({ status: 'timeout' }), timeoutMs);
  });
}

/* ===================== taking one down ============================= */

export interface RemovePhotoResult {
  ok: boolean;
  /** Where the request for a new photo went, when one was asked for. */
  notified?: { email: string | null; portalNotices: number; emailSkipped?: string } | null;
  parentNoticeError?: string | null;
}

/**
 * Take a photo down. With `notifyParent`, the parent is also told it did
 * not meet the standard and asked for a new one (email + Parent Portal
 * notice), in the words of `message` — {child}, {school} and {link} are
 * filled in by the server.
 */
export async function removeStudentPhoto(
  studentId: string,
  reason: string,
  opts?: { notifyParent?: boolean; message?: string },
): Promise<RemovePhotoResult> {
  const res = await httpsCallable(functions, 'removeStudentPhoto')({ studentId, reason, ...(opts ?? {}) });
  return (res.data ?? { ok: true }) as RemovePhotoResult;
}

/* ===================== showing one ================================= */

/*
 * One object URL per stored file and version, shared by every avatar on
 * the page. Fetching the same thumbnail forty times for a class list would
 * be forty trips through the rules for identical bytes.
 */
const cache = new Map<string, Promise<string | null>>();

function loadObjectUrl(path: string, version: number): Promise<string | null> {
  const key = `${path}@${version}`;
  let p = cache.get(key);
  if (!p) {
    p = getBlob(ref(storage, path))
      .then((b) => URL.createObjectURL(b))
      .catch(() => {
        cache.delete(key); // let a later render try again
        return null;
      });
    cache.set(key, p);
  }
  return p;
}

/**
 * The displayable URL for a pupil's photo, or null while loading, when
 * there is none, or when this viewer may not see it.
 */
export function useStudentPhotoUrl(
  photo: StudentPhoto | undefined | null,
  size: 'thumb' | 'full' = 'thumb',
): string | null {
  const path = photo ? (size === 'full' ? photo.path : photo.thumbPath) : '';
  const version = photo?.version ?? 0;
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    if (!path) return undefined;
    loadObjectUrl(path, version).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [path, version]);

  return url;
}
